import React, { useState, useEffect, useRef, useCallback } from 'react';
import { userAPI } from '../../services/api';
import { getLeadPoolAssignableRoleParams } from '../../utils/leadPoolRoles';

/**
 * Searchable user dropdown with infinite scroll pagination.
 * Fetches from GET /api/users/assignable — no elevated permissions required.
 *
 * Props:
 *   value        — currently selected user ID (string)
 *   onChange     — (userId: string, displayName: string) => void
 *   displayLabel — pre-filled display name for edit mode
 *   disabled     — boolean
 *   error        — boolean, renders red border
 *   placeholder  — string
 *   placeholder  — string
 *   staticUsers  — optional pre-loaded user list (skips API; filters locally)
 *   roles        — optional comma-separated role codes (overrides lead pool global config)
 */
const SearchableUserDropdown = ({
  value,
  onChange,
  displayLabel = '',
  disabled = false,
  error = false,
  placeholder = 'Select user',
  staticUsers = null,
  roles = null,
  triggerClassName = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(displayLabel);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownUidRef = useRef(null);
  const listRef = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchingRef = useRef(false);
  const currentSearchRef = useRef('');
  const fetchGenerationRef = useRef(0);

  const useStaticUsers = Array.isArray(staticUsers);

  const roleParams = useCallback(() => {
    if (roles) {
      return { roles };
    }
    return getLeadPoolAssignableRoleParams();
  }, [roles]);

  const filterStaticUsers = useCallback((searchTerm) => {
    const term = searchTerm.trim().toLowerCase();
    const source = staticUsers || [];
    if (!term) return source;
    return source.filter((user) => {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [staticUsers]);

  // Sync displayLabel / value from parent
  useEffect(() => {
    if (displayLabel) {
      setSelectedLabel(displayLabel);
    } else if (!value) {
      setSelectedLabel('');
    }
  }, [displayLabel, value]);

  // Auto-resolve label from API when value is set but no displayLabel was provided
  useEffect(() => {
    if (!value || displayLabel || useStaticUsers) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await userAPI.getAssignableUsers({ ids: String(value), limit: 1 });
        const { users: found = [] } = response.data?.data || {};
        if (!cancelled && found.length > 0) {
          const u = found[0];
          const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '';
          setSelectedLabel(name);
        }
      } catch {
        // silently ignore — label stays empty
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Reset list when static user source changes (e.g. lead pool filter)
  useEffect(() => {
    if (useStaticUsers) {
      setUsers(filterStaticUsers(currentSearchRef.current));
      setPagination({ page: 1, pages: 1, total: (staticUsers || []).length });
    }
  }, [useStaticUsers, staticUsers, filterStaticUsers]);

  const fetchUsers = useCallback(async (searchTerm, page, append) => {
    if (useStaticUsers) {
      const filtered = filterStaticUsers(searchTerm);
      setUsers(filtered);
      setPagination({ page: 1, pages: 1, total: filtered.length });
      return;
    }
    if (append && fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    const generation = append ? fetchGenerationRef.current : ++fetchGenerationRef.current;
    try {
      const response = await userAPI.getAssignableUsers({
        ...roleParams(),
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(value && page === 1 ? { ids: String(value) } : {}),
        page,
        limit: 50
      });
      if (generation !== fetchGenerationRef.current) return;
      const { users: newUsers = [], pagination: pag = {} } = response.data?.data || {};
      setUsers(prev => {
        const combined = append ? [...prev, ...newUsers] : newUsers;
        const seen = new Set();
        return combined.filter(u => {
          const id = String(u._id);
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      });
      setPagination({ page: pag.page || 1, pages: pag.pages || 1, total: pag.total || 0 });
    } catch (err) {
      console.error('SearchableUserDropdown: fetch error', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [useStaticUsers, filterStaticUsers, roleParams, value]);

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    document.dispatchEvent(new CustomEvent('slim:dropdownOpened', { detail: dropdownUidRef }));
    if (users.length === 0 && !loading) {
      currentSearchRef.current = '';
      fetchUsers('', 1, false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const focusInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    focusInput();
    const raf = window.requestAnimationFrame(focusInput);
    const timer = setTimeout(focusInput, 30);
    return () => {
      window.cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [isOpen]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      currentSearchRef.current = val;
      if (useStaticUsers) {
        fetchUsers(val, 1, false);
        return;
      }
      setUsers([]);
      setPagination({ page: 1, pages: 1, total: 0 });
      fetchUsers(val, 1, false);
    }, useStaticUsers ? 150 : 300);
  };

  // Scroll-based pagination (API mode only)
  const handleScroll = () => {
    if (useStaticUsers) return;
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom && !loading && !fetchingRef.current && pagination.page < pagination.pages) {
      fetchUsers(currentSearchRef.current, pagination.page + 1, true);
    }
  };

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    const handleOtherOpen = (e) => { if (e.detail !== dropdownUidRef) { setIsOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', handler);
    document.addEventListener('slim:dropdownOpened', handleOtherOpen);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('slim:dropdownOpened', handleOtherOpen);
    };
  }, []);

  const handleSelect = (user) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    setSelectedLabel(name);
    // Pass the full user object as a 3rd argument so callers that need to snapshot
    // firstName / lastName / email for cross-DB storage (e.g. ReportTargetEditor) can do so.
    onChange(String(user._id), name, user);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    setUsers([]);
    onChange('', '', null);
  };

  const hasMore = !useStaticUsers && pagination.page < pagination.pages;

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className={triggerClassName != null
          ? triggerClassName
          : `w-full rounded-lg border px-3 py-2 text-sm flex items-center justify-between text-left transition-all ${
              error
                ? 'bg-red-50 ring-2 ring-red-500 border-red-300'
                : 'bg-gray-50 border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-white'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`
        }
      >
        <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {selectedLabel && !disabled && (
            <span
              onClick={handleClear}
              className="text-gray-300 hover:text-gray-500 leading-none text-base"
              title="Clear selection"
            >
              ×
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[240px]">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <svg
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                autoFocus
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by name or email..."
                className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50"
              />
            </div>
            {pagination.total > 0 && (
              <p className="text-xs text-gray-400 mt-1">{pagination.total} user{pagination.total !== 1 ? 's' : ''}</p>
            )}
          </div>

          {/* List */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-52 overflow-y-auto"
          >
            {!loading && users.length === 0 && (
              <p className="px-3 py-4 text-xs text-gray-500 text-center">
                {search ? 'No users match your search' : 'No users found'}
              </p>
            )}

            {users.map(user => {
              const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
              const isSelected = String(user._id) === String(value);
              return (
                <button
                  key={user._id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className={`w-full flex flex-col px-3 py-2 text-left transition-colors ${
                    isSelected
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm font-medium leading-tight">{name}</span>
                  <span className="text-xs text-gray-400 leading-tight truncate">{user.email}</span>
                </button>
              );
            })}

            {loading && (
              <div className="flex items-center justify-center py-3">
                <svg className="animate-spin w-3 h-3 text-blue-600 mr-1.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="text-xs text-gray-500">Loading...</span>
              </div>
            )}

            {!loading && hasMore && (
              <p className="text-center text-xs text-gray-400 py-1.5 border-t border-gray-100">
                Scroll for more results
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableUserDropdown;
